/**
 * Aba "Visão geral" do Cliente 360. Espelha o mockup aprovado:
 * banner de Próxima ação · col-main [Pedidos recentes + Atividade recente |
 * Health do relacionamento + Próximos vencimentos] · Dados cadastrais (2-col
 * + Copiar dados). Tudo com dado real do cockpit; o que ainda não tem fonte
 * (RG/nascimento/vencimentos) aparece como "—" / vazio honesto.
 */
import React from "react";
import {
  FiActivity,
  FiAlertCircle,
  FiArrowRight,
  FiCalendar,
  FiCheck,
  FiClock,
  FiCopy,
  FiHeart,
  FiPackage,
  FiUser,
  FiZap,
} from "react-icons/fi";

import { useToast } from "../../../../context/ToastContext";
import { mascaraDocumento } from "../../../../utils/documento";
import { formatarEndereco } from "../../../../utils/endereco";
import HealthRing from "../../HealthRing";
import {
  Badge,
  Field,
  fmtBRLCheio,
  fmtDataLonga,
  fmtDataHora,
  Panel,
  ScorePill,
} from "../cli360";
import { enderecoPrincipal, enderecosDoCliente } from "../enderecosCliente";

const TIER_GRADE = {
  saudavel: "Saudável",
  atencao: "Atenção",
  em_risco: "Em risco",
  sem_score: "Sem histórico",
};

function pedidoTone(situacao, display) {
  const s = (situacao || display || "").toUpperCase();
  if (s.includes("CANCEL")) return "danger";
  if (s.includes("ENTREG") && !s.includes("EM_ENTREG")) return "success";
  if (s.includes("CONCLU")) return "success";
  if (s.includes("EXPED") || s.includes("ENTREG")) return "info";
  if (s.includes("PRODUC") || s.includes("PRODUÇ")) return "warning";
  return "neutral";
}

function tomToTl(tom) {
  if (tom === "success") return "ok";
  if (tom === "warning") return "warn";
  if (tom === "danger") return "warn";
  if (tom === "neutral") return "muted";
  return "";
}

function tlIcon(tone) {
  if (tone === "ok") return <FiCheck aria-hidden="true" />;
  if (tone === "warn") return <FiAlertCircle aria-hidden="true" />;
  if (tone === "muted") return <FiClock aria-hidden="true" />;
  return <FiActivity aria-hidden="true" />;
}

function tituloDoEndereco(endereco, posicao) {
  return String(endereco?.titulo || "").trim() || `Endereço ${posicao + 1}`;
}

function VisaoGeralTab({
  cliente,
  saude,
  kpis,
  pedidos = [],
  atendimentos = [],
  parceria,
  origem,
  onGotoTab,
}) {
  const toast = useToast();

  const proxima = kpis?.proxima_acao;
  const pf = (cliente?.tipo || "PF") !== "PJ";
  // Endereços titulados (ADR-0071): o 1º é o principal; sem nenhum marcado
  // como cobrança, a cobrança usa o principal (decisão do dono, 2026-09-15).
  const enderecos = enderecosDoCliente(cliente);
  const cobrancaMarcada = enderecos.some((endereco) => endereco?.cobranca);

  const copiarDados = async () => {
    const texto = [cliente?.nome, cliente?.telefone, formatarEndereco(enderecoPrincipal(cliente))]
      .filter(Boolean)
      .join("\n");
    try {
      await navigator.clipboard.writeText(texto);
      toast.success("Dados copiados!");
    } catch {
      toast.error("Não consegui copiar — copie manualmente.");
    }
  };

  const pedidosRecentes = pedidos.slice(0, 3);
  const atividade = atendimentos.slice(0, 5);

  const consentTone = { execucao_contrato: "info", legitimo_interesse: "neutral", consentimento: "success", obrigacao_legal: "neutral" };
  const consentLabel = {
    execucao_contrato: "Execução de contrato",
    legitimo_interesse: "Legítimo interesse",
    consentimento: "Consentimento",
    obrigacao_legal: "Obrigação legal",
  };

  return (
    <section className="tab-panel">
      {/* Próxima ação */}
      {proxima?.texto ? (
        <div
          className="panel"
          style={{
            borderColor: "color-mix(in srgb, var(--accent) 38%, var(--border))",
            background: "linear-gradient(95deg, var(--accent-soft), transparent 55%)",
            marginBottom: 16,
          }}
        >
          <div className="flex between center" style={{ padding: "15px 20px", gap: 16, flexWrap: "wrap" }}>
            <div className="flex center" style={{ gap: 14, minWidth: 0 }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: "var(--accent)",
                  display: "grid",
                  placeItems: "center",
                  color: "#fff",
                  flexShrink: 0,
                }}
              >
                <FiZap size={20} aria-hidden="true" />
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="eyebrow" style={{ color: "var(--accent)" }}>
                  Próxima ação{proxima.data ? " · agendada" : ""}
                </div>
                <div style={{ fontWeight: 600, fontSize: 15, marginTop: 3 }}>{proxima.texto}</div>
                {/* O cockpit manda `responsavel_nome` (CRM-43); a tela lia
                    `responsavel`, que nunca existiu — e o nome so aparecia
                    quando havia data agendada. */}
                {proxima.data || proxima.responsavel_nome ? (
                  <div className="muted sm" style={{ marginTop: 2 }}>
                    {[proxima.data ? fmtDataLonga(proxima.data) : null, proxima.responsavel_nome]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                ) : null}
              </div>
            </div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => onGotoTab("historico")}>
              Abrir no histórico <FiArrowRight size={13} aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}

      <div className="col-main">
        {/* ESQUERDA */}
        <div className="stack" style={{ gap: 16 }}>
          <Panel
            title="Pedidos recentes"
            icon={<FiPackage aria-hidden="true" />}
            action={
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => onGotoTab("pedidos")}>
                Ver todos <FiArrowRight size={13} aria-hidden="true" />
              </button>
            }
          >
            {pedidosRecentes.length === 0 ? (
              <div className="panel-body muted sm">Nenhum pedido ainda.</div>
            ) : (
              <table className="dtable">
                <thead>
                  <tr>
                    <th>Pedido</th>
                    <th>Ambiente</th>
                    <th>Status</th>
                    <th className="num">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {pedidosRecentes.map((p) => (
                    <tr key={p.id}>
                      <td className="cmono">{p.numero}</td>
                      <td>{(p.ambientes || []).map((a) => a.nome).join(", ") || `${p.ambientes_count || 0} ambientes`}</td>
                      <td>
                        <Badge tone={pedidoTone(p.situacao, p.situacao_display)} dot>
                          {p.situacao_display}
                        </Badge>
                      </td>
                      <td className="num">{fmtBRLCheio(p.valor_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>

          <Panel
            title="Atividade recente"
            icon={<FiActivity aria-hidden="true" />}
            action={
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => onGotoTab("historico")}>
                Histórico completo <FiArrowRight size={13} aria-hidden="true" />
              </button>
            }
          >
            <div className="panel-body">
              {atividade.length === 0 ? (
                <div className="muted sm">Nenhum contato registrado.</div>
              ) : (
                <div className="tl">
                  {atividade.map((a) => {
                    const tone = tomToTl(a.tom);
                    return (
                      <div className="tl-item" key={a.id}>
                        <div className={`tl-dot${tone ? ` ${tone}` : ""}`}>{tlIcon(tone)}</div>
                        <div className="tl-title">{a.assunto || a.tipo_display}</div>
                        {/* `resultado` e o codigo interno ("SEM_RETORNO");
                            quem le a tela precisa do rotulo (CRM-43). */}
                        {a.resultado_display ? <div className="tl-text">{a.resultado_display}</div> : null}
                        <div className="tl-time">
                          {fmtDataHora(a.data)}
                          {a.responsavel_nome ? ` · ${a.responsavel_nome}` : ""}
                          {a.canal_display ? ` · ${a.canal_display}` : ""}
                          {a.origem_lead_numero ? (
                            <span className="tag" style={{ marginLeft: 6 }}>
                              do lead
                            </span>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Panel>
        </div>

        {/* DIREITA */}
        <div className="stack" style={{ gap: 16 }}>
          <Panel title="Health do relacionamento" icon={<FiHeart aria-hidden="true" />}>
            <div className="panel-body">
              <div style={{ display: "flex", justifyContent: "center" }}>
                <HealthRing score={saude?.score} tier={saude?.tier} size={148} />
              </div>
              <div className="tiny muted" style={{ textAlign: "center", margin: "8px 0 14px" }}>
                {TIER_GRADE[saude?.tier] || "Sem histórico"}
              </div>
              {(saude?.breakdown || []).map((f) => (
                <div className="hrow" key={f.chave} title={f.nota || undefined}>
                  <span>{f.label}</span>
                  {f.pontos != null ? (
                    <ScorePill tone={f.tom || "neutral"}>
                      {f.pontos > 0 ? "+" : ""}
                      {f.pontos}
                    </ScorePill>
                  ) : (
                    <span className="tiny muted mono">{f.nota || "—"}</span>
                  )}
                </div>
              ))}
            </div>
          </Panel>

          <Panel
            title="Próximos vencimentos"
            icon={<FiCalendar aria-hidden="true" />}
            action={
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => onGotoTab("financeiro")}>
                Financeiro <FiArrowRight size={13} aria-hidden="true" />
              </button>
            }
          >
            <div className="panel-body muted sm">
              Os vencimentos por cliente aparecem aqui assim que a cobrança estiver vinculada.
            </div>
          </Panel>
        </div>
      </div>

      {/* Dados cadastrais */}
      <Panel
        title="Dados cadastrais"
        icon={<FiUser aria-hidden="true" />}
        action={
          <button type="button" className="btn btn-secondary btn-sm" onClick={copiarDados}>
            <FiCopy size={13} aria-hidden="true" /> Copiar dados
          </button>
        }
        style={{ marginTop: 16 }}
      >
        <div className="panel-body grid-2" style={{ gap: 28 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              {pf ? "Pessoa física" : "Pessoa jurídica"}
            </div>
            <div className="fields">
              {pf ? (
                <>
                  <Field label="Nome completo">{cliente?.nome || "—"}</Field>
                  <Field label="CPF" mono role="group" aria-label="CPF">
                    {mascaraDocumento(cliente?.documento, cliente?.tipo) || "—"}
                  </Field>
                  <Field label="RG" mono>{cliente?.rg || "—"}</Field>
                  <Field label="Nascimento" mono>{fmtDataLonga(cliente?.data_nascimento) || "—"}</Field>
                  <Field label="Estado civil">{cliente?.estado_civil || "—"}</Field>
                  <Field label="Profissão">{cliente?.profissao || "—"}</Field>
                  <Field label="E-mail" mono>{cliente?.email || "—"}</Field>
                  <Field label="Telefone" mono>{cliente?.telefone || "—"}</Field>
                </>
              ) : (
                <>
                  <Field label="Razão social">{cliente?.nome || "—"}</Field>
                  <Field label="Nome fantasia">{cliente?.nome_fantasia || "—"}</Field>
                  <Field label="CNPJ" mono role="group" aria-label="CNPJ">
                    {mascaraDocumento(cliente?.documento, cliente?.tipo) || "—"}
                  </Field>
                  <Field label="Inscrição estadual" mono>{cliente?.inscricao_estadual || "—"}</Field>
                  <Field label="E-mail" mono>{cliente?.email || "—"}</Field>
                  <Field label="Telefone" mono>{cliente?.telefone || "—"}</Field>
                </>
              )}
            </div>
          </div>
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              Endereços & origem
            </div>
            <div className="fields">
              {/* Cada endereço é um grupo nomeado pelo título: o leitor de tela
                  anuncia "Casa" junto da linha e dos selos. */}
              {enderecos.length === 0 ? (
                <Field label="Endereço" role="group" aria-label="Endereço">—</Field>
              ) : (
                enderecos.map((endereco, posicao) => (
                  <Field
                    key={endereco.id || posicao}
                    label={tituloDoEndereco(endereco, posicao)}
                    role="group"
                    aria-label={tituloDoEndereco(endereco, posicao)}
                  >
                    {formatarEndereco(endereco) || "—"}
                    {posicao === 0 ? (
                      <span className="tag" style={{ marginLeft: 6 }}>
                        Principal
                      </span>
                    ) : null}
                    {endereco.cobranca ? (
                      <span className="tag" style={{ marginLeft: 6 }}>
                        Cobrança
                      </span>
                    ) : null}
                  </Field>
                ))
              )}
              {enderecos.length > 0 && !cobrancaMarcada ? (
                <Field label="Cobrança" role="group" aria-label="Cobrança">
                  Mesma do principal
                </Field>
              ) : null}
              <Field label="Indicado por">
                {parceria?.representante_nome ? (
                  <>
                    {parceria.representante_nome}{" "}
                    <span className="tag">parceiro</span>
                  </>
                ) : (
                  "—"
                )}
              </Field>
              <Field label="Origem">
                {origem?.rotulo || cliente?.origem_legado || "Cadastro direto"}
                {origem?.fonte === "lead" && origem?.lead_numero ? (
                  <span className="tag" style={{ marginLeft: 6 }}>
                    via {origem.lead_numero}
                  </span>
                ) : null}
              </Field>
              <Field label="Cadastro" mono>{fmtDataLonga(cliente?.created_at) || "—"}</Field>
              <Field label="Consent. LGPD">
                {cliente?.base_legal ? (
                  <Badge tone={consentTone[cliente.base_legal] || "neutral"}>
                    {consentLabel[cliente.base_legal] || cliente.base_legal}
                  </Badge>
                ) : (
                  "—"
                )}
              </Field>
            </div>
          </div>
        </div>
      </Panel>
    </section>
  );
}

export default VisaoGeralTab;
